import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Calculator,
  ListOrdered,
  Map as MapIcon,
  Route as RouteIcon,
  Scale,
  ShieldAlert,
} from "lucide-react";

import { seo } from "@/lib/seo";
import { getCaseStudies, getTestimonials } from "@/lib/content";
import {
  ServicePageLayout,
  serviceJsonLd,
  type ServicePageContent,
} from "@/components/service/ServicePageLayout";

const SERVICE_TAG = "ai-opportunity-assessment";
const PATH = "/services/ai-opportunity-assessment";
const NAME = "AI Opportunity Assessment";
const TITLE = "Small Business AI Opportunity Assessment";
const SERVICE_TYPE = "AI opportunity assessment for small business";
const DESCRIPTION =
  "An AI readiness and opportunity assessment for small businesses: a ranked list of where AI pays in your operations, with impact, effort and payback.";

const FAQS = [
  {
    q: "What is an AI opportunity assessment?",
    a: "An AI opportunity assessment is a structured review of your day-to-day operations. We map how work moves through the business, find the points where time, errors, or slow responses are costing you, and rank the places where AI or automation could help. The output is a practical list: what to do first, what to avoid, what data or systems are needed, and what would make the project worth doing.",
  },
  {
    q: "How is this different from an AI readiness assessment?",
    a: "An AI readiness assessment usually asks whether your data, systems, people, and governance are prepared for AI. We include that, but we do not stop there. The assessment also looks for specific operating problems where AI may pay. Readiness tells you whether you can move. Opportunity tells you where to move first.",
  },
  {
    q: "Do we need to have clean data first?",
    a: "No. We need to see the work clearly enough to judge it. Messy data is often one of the findings, and sometimes the right first project is a cleanup workflow rather than an AI build. If the data is too scattered for a useful implementation, that goes into the recommendation instead of being hidden.",
  },
  {
    q: "What if the assessment says AI is not worth it for us?",
    a: "Then that is the result. Some processes are too low-volume, too variable, too risky, or already handled well by existing software. A useful assessment can save you from buying or building something you do not need. The report will still show what we looked at and why we do not recommend spending there now.",
  },
  {
    q: "Can we do the assessment ourselves with the free scorecard?",
    a: "Yes, if you want the self-serve version. The scorecard helps you think through the same six operating areas and gives a quick read on likely fit. The paid assessment goes inside the business: interviews, workflow review, systems walkthroughs, and a ranked register you can use to make a decision.",
  },
  {
    q: "How long does an assessment take?",
    a: "It is measured in days or a few weeks, not quarters. The timing depends on how many functions we need to review and how quickly the right people can walk us through the work. We set the schedule in the written scope before the engagement starts.",
  },
  {
    q: "What do you need from us?",
    a: "We need access to the people who actually do the work, a walkthrough of the tools they use, samples of ordinary work where appropriate, and a decision-maker who will act on the findings. We do not need a major data project before the assessment begins.",
  },
  {
    q: "Who should be involved in the assessment?",
    a: "The most useful conversations are usually with operators, managers, and one person who can decide what gets built. Senior leaders can describe priorities, but the people doing the work know where delays, rework, and exceptions really happen.",
  },
  {
    q: "Do we have to use you to build it?",
    a: "No. The report is written so it can be used by your team or another implementation partner. We can build what ranks first, but the assessment has to stand on its own or it is not useful advice.",
  },
  {
    q: "Will the assessment recommend specific AI tools?",
    a: "Only when a tool choice is genuinely part of the decision. The first recommendation is usually a workflow, a business rule, a data requirement, or a build-or-buy call. A tool list without that context is shopping, not consulting.",
  },
] as const;

const CONTENT: ServicePageContent = {
  path: PATH,
  name: NAME,
  eyebrow: "AI Opportunity Assessment",
  h1: "AI opportunity assessment: find where AI pays before you build it.",
  lead: "A structured review of how your business actually runs, ending in a ranked list of AI opportunities with impact, effort, and time to payback for each one. You own the output whether or not we build any of it.",
  problem: {
    heading: "The expensive way to learn AI doesn't fit is to build it first.",
    paragraphs: [
      "Most AI spending starts with a tool rather than a problem. Someone sees a demo, a pilot gets funded, and a few weeks later there is a working prototype that no part of the business depends on.",
      "That failure is rarely technical. The project was never anchored to a process where the company was losing time, money, or attention in a way people could point to. Without that anchor, there is no serious way to judge whether the AI helped.",
      "An assessment reverses the order. We start with your operations and work outward to the technology, so the first thing you build is tied to work that already matters. Sometimes the answer is AI. Sometimes it is a simple rule, a form, a better handoff, or no project at all.",
    ],
  },
  useCases: {
    heading: "Where an assessment looks inside a small business.",
    intro:
      "The assessment follows the work, not a software category. These are the six operating areas we review because they are where repeated work, customer delays, and manual judgment usually show up first.",
    groups: [
      {
        area: "Customer communications",
        examples: [
          "A shared inbox where the same six questions get typed out by hand every day.",
          "Lead follow-up that depends on whoever notices the message first.",
          "Support requests that need triage before a human can give a useful answer.",
          "Call notes that never make it into the customer record.",
        ],
      },
      {
        area: "Documents and data",
        examples: [
          "Invoices, forms, contracts, or applications re-keyed from PDFs into another system.",
          "Scanned documents that need classification before anyone can work them.",
          "Spreadsheet cleanup that happens before every report or handoff.",
          "Data fields that are missing because nobody owns the intake step.",
        ],
      },
      {
        area: "Sales and proposals",
        examples: [
          "Proposal drafts assembled from old files with inconsistent language and pricing notes.",
          "Sales questions that require searching through past emails before anyone can respond.",
          "Quotes that wait while someone checks availability, requirements, or approval rules.",
          "Follow-up sequences that stop when the salesperson gets busy.",
        ],
      },
      {
        area: "Scheduling",
        examples: [
          "Appointment requests handled by back-and-forth messages instead of a clear path.",
          "Rescheduling that depends on one person knowing the constraints in their head.",
          "Dispatch or staff assignment decisions made from stale calendars.",
          "Missed reminders that create avoidable customer service work.",
        ],
      },
      {
        area: "Knowledge",
        examples: [
          "Answers buried in policies, training notes, chat threads, or one long-tenured employee's memory.",
          "New hires asking the same operational questions because the guide is out of date.",
          "Managers rewriting the same explanation for different teams.",
          "Internal search that returns documents but not a usable answer.",
        ],
      },
      {
        area: "Finance and admin",
        examples: [
          "Bills and receipts copied into the accounting system by hand.",
          "Monthly reporting assembled from exports, tabs, and email attachments.",
          "Grant, loan, or compliance packets checked manually against a requirements list.",
          "Approvals that stall because nobody can see what is missing.",
        ],
      },
    ],
  },
  cards: [
    {
      icon: MapIcon,
      title: "Operations map",
      body: "How work actually flows through your business, including the steps nobody has written down.",
    },
    {
      icon: ListOrdered,
      title: "Ranked opportunity register",
      body: "Every candidate we find, scored on impact, effort, and time to value.",
    },
    {
      icon: Calculator,
      title: "Payback estimates",
      body: "What each opportunity is plausibly worth, and what it would cost to get there.",
    },
    {
      icon: ShieldAlert,
      title: "Readiness and risk notes",
      body: "Where your data, systems, or team would block an implementation, before it does.",
    },
    {
      icon: RouteIcon,
      title: "Recommended sequence",
      body: "What to do first, what to do next, and what to leave alone for now.",
    },
    {
      icon: Scale,
      title: "Build-or-buy guidance",
      body: "Where an off-the-shelf tool is genuinely enough, and where it isn't.",
    },
  ],
  deliverables: {
    heading: "What you actually receive.",
    intro: "Everything is yours to keep and act on, with us or with any other firm.",
    items: [
      "A written assessment report",
      "The ranked opportunity register as a working spreadsheet",
      "A one-page summary for stakeholders who will not read the report",
      "A walkthrough session with the consultant who did the work",
      "Your operations map, documented",
    ],
  },
  readiness: {
    heading: "When an assessment is worth buying.",
    ready: [
      "You can describe how work actually moves through the business, even if it is messy.",
      "You have repeated tasks, slow handoffs, or customer delays that people already complain about.",
      "Someone with authority will decide what happens after the findings are delivered.",
      "The team is willing to show the real process, not just the version in the handbook.",
      "You want a ranked set of choices before committing to a build.",
    ],
    notYet: [
      "You want a vendor to tell you what software to buy without looking at the work.",
      "Nobody owns operations or can decide which process matters most.",
      "The business is changing so quickly that the workflow will be different next week.",
      "The goal is to chase AI because competitors mention it, not because a process is hurting.",
      "You already know the exact system you need and only want someone to build it.",
    ],
  },
  process: {
    heading: "How the assessment runs.",
    steps: [
      {
        n: "1",
        title: "Discovery",
        body: "We learn what you do, which functions are under pressure, and whether an assessment is the right next step.",
      },
      {
        n: "2",
        title: "Operations review",
        body: "Interviews with the people doing the work, plus a walkthrough of the systems they use.",
      },
      {
        n: "3",
        title: "Analysis and ranking",
        body: "We score every candidate opportunity and pressure-test the ones that look attractive.",
      },
      {
        n: "4",
        title: "Walkthrough and handover",
        body: "We present the findings, argue them with you, and hand over everything.",
      },
    ],
  },
  timeline: {
    heading: "How long it takes.",
    body: "Most assessment work is measured in days or a few weeks. The real driver is not company size; it is how many functions need review and how quickly the right people can walk through the work. We keep the schedule vague until scoping because a rushed assessment produces a tidy report and bad decisions.",
  },
  pricing: {
    heading: "What it costs.",
    body: "Assessments are fixed-quote, scoped in writing after the free call, so the price is known before any work starts.",
  },
  related: {
    heading: "Where to start, and what usually follows.",
    body: (
      <>
        <p>
          Not sure this is the right starting point? The{" "}
          <Link to="/tools/ai-opportunity-scorecard">free AI readiness scorecard</Link> takes a few
          minutes and gives you a rough read on where AI could pay, before you talk to anyone.
        </p>
        <p>
          When an assessment does find something worth doing, the next step is usually{" "}
          <Link to="/services/ai-implementation">AI implementation</Link> for whatever ranked first,
          and then <Link to="/services/ai-enablement">AI training and adoption</Link> once the
          system is live and people have to change how they work.
        </p>
      </>
    ),
  },
  faqs: FAQS,
  cta: {
    heading: "Start with a conversation, not a contract.",
    line: "A short call is enough for us to say whether an assessment is worth your money, including when it is not.",
  },
};

export const Route = createFileRoute("/_site/services/ai-opportunity-assessment")({
  head: () =>
    seo({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      jsonLd: serviceJsonLd({
        path: PATH,
        name: NAME,
        description: DESCRIPTION,
        serviceType: SERVICE_TYPE,
        faqs: FAQS,
      }),
    }),
  loader: async () => {
    const [caseStudies, testimonials] = await Promise.all([
      getCaseStudies({ data: {} }),
      getTestimonials({ data: { limit: 3 } }),
    ]);
    return {
      caseStudies: caseStudies.filter((study) => study.services?.includes(SERVICE_TAG)).slice(0, 3),
      testimonials: testimonials.filter((t) => !t.service_tag || t.service_tag === SERVICE_TAG),
    };
  },
  component: Page,
  errorComponent: () => <ServicePageLayout content={CONTENT} />,
  notFoundComponent: () => null,
});

function Page() {
  const { caseStudies, testimonials } = Route.useLoaderData();
  return (
    <ServicePageLayout content={CONTENT} caseStudies={caseStudies} testimonials={testimonials} />
  );
}
